<template>
  <transition name="modal-fade">
    <div v-if="show" class="call-modal-overlay" :class="{ 'dragging-active': !isInitialPosition }">
      <div
        ref="modalRef"
        class="call-modal"
        :class="{ 'is-dragging': isDragging }"
        :style="modalStyle"
        @click.stop
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <!-- Header -->
        <div class="call-header" :class="{ draggable: true }">
          <div class="call-header-identity">
            <div class="call-header-avatar">
              <span v-if="contactInitials" class="call-header-initials">{{ contactInitials }}</span>
              <Icon v-else icon="mdi:phone-in-talk" width="18" height="18" />
            </div>
            <div class="call-header-text">
              <h4 class="call-header-name">{{ contactName || to || "Call Session" }}</h4>
              <div class="call-header-subtitle">
                <span
                  v-if="headerStatusDot"
                  class="call-header-status-dot"
                  :class="headerStatusDot"
                ></span>
                <span class="call-header-subtitle-text">{{ headerSubtitle }}</span>
              </div>
            </div>
          </div>
          <div class="call-header-actions">
            <button type="button" class="header-icon-btn drag-handle" title="Drag to move">
              <Icon icon="mdi:drag" width="18" height="18" />
            </button>
            <button
              type="button"
              class="header-icon-btn btn-close-call"
              aria-label="Close"
              @click="close"
            >
              <Icon icon="mdi:close" width="18" height="18" />
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="call-body">
          <!-- Incoming Call Section -->
          <div
            v-if="incomingCallData && incomingCallData.callState === 'ringing'"
            class="incoming-call-section"
          >
            <div class="incoming-call-header">
              <div class="incoming-call-indicator">
                <span class="pulse-dot"></span>
                <span class="incoming-call-text">Incoming Call</span>
              </div>
              <div class="incoming-call-from">{{ incomingCallData.from }}</div>
            </div>
            <div class="incoming-call-actions">
              <Button
                variant="danger"
                style="flex: 1"
                icon="mdi:phone-hangup"
                @click="handleDeclineIncoming"
              >
                Decline
              </Button>
              <Button
                variant="success"
                style="flex: 1"
                icon="mdi:phone"
                @click="handleAnswerIncoming"
              >
                Answer
              </Button>
            </div>
          </div>

          <div
            v-if="incomingCallData && incomingCallData.callState === 'active'"
            class="active-incoming-call-section"
          >
            <div class="active-call-header">
              <div class="active-call-indicator">
                <span class="active-dot"></span>
                <span class="active-call-text">Active Call</span>
              </div>
              <div class="active-call-timer">{{ formattedIncomingCallTimer }}</div>
            </div>
            <div class="active-call-actions">
              <Button
                variant="danger"
                style="flex: 1"
                icon="mdi:phone-hangup"
                @click="$emit('hangup-incoming-call')"
              >
                Hangup
              </Button>
            </div>
            <div
              v-if="enableTranscription && incomingCallData.callSid"
              class="incoming-call-caption-box"
            >
              <LiveCaption
                :is-active="true"
                :enabled="enableTranscription"
                :call-sid="incomingCallData.callSid"
              />
            </div>
          </div>

          <!-- AI Pre-Call Prompt -->
          <div
            v-if="!incomingCallData && (showAIPrompt || isPreparingAIContext)"
            class="ai-call-prompt-overlay"
          >
            <div class="ai-call-prompt-card">
              <template v-if="isPreparingAIContext">
                <div class="ai-call-prompt-header">
                  <Icon icon="mdi:robot-outline" width="20" height="20" class="ai-icon" />
                  <span class="ai-header-title">Preparing AI Briefing</span>
                </div>
                <div class="ai-call-prompt-loading">
                  <Spinner size="md" variant="primary" />
                  <div class="ai-call-prompt-loading-text">
                    <p class="ai-call-prompt-text muted">
                      Analyzing contact data and preparing talking points...
                    </p>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="ai-call-prompt-header">
                  <Icon icon="mdi:robot-outline" width="20" height="20" class="ai-icon" />
                  <span class="ai-header-title">AI Call Assistant</span>
                </div>
                <p class="ai-call-prompt-text">
                  Unlock real-time strategic suggestions, talking points, and sentiment analysis
                  during your conversation.
                </p>
                <div class="ai-call-prompt-icp-selection" v-if="icps && icps.length > 0">
                  <Select
                    v-model="selectedIcpId"
                    :options="icpOptions"
                    label="Target ICP"
                    size="md"
                    :clearable="false"
                    leading-icon="mdi:bullseye-arrow"
                  />
                </div>
                <div class="ai-call-prompt-actions">
                  <Button
                    variant="primary"
                    style="flex: 1"
                    icon="mdi:sparkles"
                    @click="$emit('proceed-with-ai', selectedIcpId)"
                  >
                    Call with AI
                  </Button>
                  <Button
                    variant="outline"
                    style="flex: 1"
                    icon="mdi:phone"
                    @click="$emit('proceed-without-ai', selectedIcpId)"
                  >
                    Call without AI
                  </Button>
                </div>
              </template>
            </div>
          </div>

          <!-- Outbound Active Call -->
          <div
            v-if="
              !incomingCallData &&
              !showAIPrompt &&
              !isPreparingAIContext &&
              activeCallStates.includes(status)
            "
            class="active-call-container"
          >
            <!-- AI Suggestion Bubble -->
            <transition name="fade-slide">
              <div v-if="hasSuggestions || isThinking || isStreaming" class="ai-suggestion-bubble">
                <div class="suggestion-header">
                  <div class="header-left">
                    <div class="robot-icon-wrapper">
                      <Icon icon="mdi:robot-outline" width="16" height="16" />
                    </div>
                    <span class="suggestion-label">AI SUGGESTION</span>
                  </div>
                </div>
                <!-- Scrollable Container -->
                <div class="ai-suggestions-list" ref="suggestionsContainer">
                  <!-- Previous and Latest Suggestions -->
                  <div
                    v-for="(suggestion, index) in aiSuggestions"
                    :key="suggestion.id"
                    class="suggestion-item"
                    :class="{
                      'is-latest':
                        index === (aiSuggestions?.length || 0) - 1 && !isStreaming && !isThinking,
                    }"
                  >
                    <p class="suggestion-text">{{ suggestion.text }}</p>
                  </div>

                  <!-- Streaming Suggestion -->
                  <div v-if="isStreaming" class="suggestion-item is-latest">
                    <p class="suggestion-text">
                      {{ currentStreamingText }}<span class="typing-cursor">|</span>
                    </p>
                  </div>

                  <!-- Thinking Loader -->
                  <div v-if="isThinking" class="thinking-item">
                    <div class="thinking-loader"><span></span><span></span><span></span></div>
                  </div>
                </div>
              </div>
            </transition>

            <!-- Live Captions Box (No uppercase label as requested) -->
            <div v-if="enableTranscription" class="live-caption-container-box">
              <LiveCaption
                :is-active="true"
                :enabled="enableTranscription"
                :call-sid="parentCallSid || currentCallSid"
              />
            </div>

            <!-- Actions Controls - Adjusted to match "Original" inline style -->
            <div class="call-controls-row">
              <div class="control-item-original">
                <Button
                  rounded
                  variant="danger"
                  icon="mdi:phone-hangup"
                  class="control-circle-btn"
                  @click="$emit('hangup')"
                />
                <span class="control-label-original">Hang up</span>
              </div>

              <div
                class="control-item-original"
                :class="{ active: account?.allowCallRecording && saveRecording }"
              >
                <Button
                  rounded
                  :variant="account?.allowCallRecording && saveRecording ? 'danger' : 'outline'"
                  icon="mdi:record-circle-outline"
                  class="control-circle-btn"
                  @click="$emit('toggle-record')"
                />
                <span class="control-label-original">Record</span>
              </div>

              <div class="control-item-original" :class="{ active: isMuted }">
                <Button
                  rounded
                  :variant="isMuted ? 'primary' : 'outline'"
                  :icon="isMuted ? 'mdi:microphone-off' : 'mdi:microphone'"
                  class="control-circle-btn"
                  @click="$emit('toggle-mute')"
                />
                <span class="control-label-original">Mute</span>
              </div>

              <div class="control-item-original" style="position: relative">
                <div ref="keypadTriggerRef">
                  <Button
                    rounded
                    :variant="showIVRDropdown ? 'primary' : 'outline'"
                    icon="mdi:dialpad"
                    class="control-circle-btn"
                    @click.stop="showIVRDropdown = !showIVRDropdown"
                  />
                </div>
                <span class="control-label-original">Keypad</span>
              </div>

              <div class="control-item-original" style="position: relative">
                <Button
                  rounded
                  :variant="showMicrophoneDropdown ? 'primary' : 'outline'"
                  icon="mdi:volume-high"
                  class="control-circle-btn"
                  @click.stop="showMicrophoneDropdown = !showMicrophoneDropdown"
                />
                <span class="control-label-original">Audio</span>
                <div v-if="showMicrophoneDropdown" class="microphone-dropdown" @click.stop>
                  <div class="dropdown-header">Select Microphone</div>
                  <ul>
                    <li
                      v-for="mic in microphones"
                      :key="mic.deviceId"
                      @click="onSelectMicrophone(mic.deviceId)"
                    >
                      {{ mic.label }}
                    </li>
                  </ul>
                </div>
              </div>

              <div class="control-item-original" style="position: relative">
                <div ref="transferTriggerRef">
                  <Button
                    rounded
                    :variant="showTransferPopover ? 'primary' : 'outline'"
                    icon="mdi:phone-forward"
                    class="control-circle-btn"
                    @click.stop="showTransferPopover = !showTransferPopover"
                  />
                </div>
                <span class="control-label-original">Transfer</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Teleported popovers -->
      <Teleport to="body">
        <div v-if="showIVRDropdown" class="keypad-popover" :style="keypadPopoverStyle" @click.stop>
          <Button
            variant="ghost"
            size="xs"
            icon="mdi:close"
            style="position: absolute; top: 8px; right: 8px"
            @click="showIVRDropdown = false"
          />
          <div class="keypad-popover-header">
            <input
              v-model="ivrInputValue"
              placeholder="Enter digits"
              class="keypad-input"
              autocomplete="off"
            />
          </div>
          <div class="keypad-grid">
            <Button
              v-for="num in ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']"
              :key="num"
              variant="outline"
              style="height: 44px; width: 100%; font-weight: 700"
              @click="handleIVRBtn(num)"
            >
              {{ num }}
            </Button>
          </div>
        </div>
      </Teleport>

      <Teleport to="body">
        <div
          v-if="showTransferPopover"
          class="transfer-popover"
          :style="transferPopoverStyle"
          @click.stop
        >
          <div class="transfer-popover__header">
            <span class="transfer-popover__title">Transfer call</span>
            <Button
              variant="ghost"
              size="xs"
              icon="mdi:close"
              @click="showTransferPopover = false"
            />
          </div>
          <p class="transfer-popover__hint">
            Forward this call to another number. The call will continue with that party and your
            line will end.
          </p>
          <input
            v-model="transferTarget"
            class="transfer-popover__input"
            placeholder="+15551234567"
            autocomplete="off"
            @keydown.enter="onSubmitTransfer"
          />
          <Button
            variant="primary"
            :disabled="!isValidTransferNumber"
            :loading="isTransferring"
            style="width: 100%; margin-top: 12px"
            @click="onSubmitTransfer"
          >
            Transfer
          </Button>
        </div>
      </Teleport>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { Icon } from "@iconify/vue";
import LiveCaption from "@/components/call/LiveCaption.vue";
import Button from "@/components/ui/Button.vue";
import Spinner from "@/components/ui/Spinner.vue";
import Select from "@/components/ui/Select.vue";

interface Props {
  show: boolean;
  contactName?: string;
  to?: string;
  status: string;
  isPreparingAIContext: boolean;
  showAIPrompt: boolean;
  incomingCallData: any;
  formattedIncomingCallTimer: string;
  formattedCallTimer: string;
  isMuted: boolean;
  saveRecording: boolean;
  enableTranscription: boolean;
  parentCallSid: string | null;
  currentCallSid: string | null;
  account: any;
  microphones: any[];
  speakers?: any[];
  selectedMicId?: string;
  selectedSpeakerId?: string;
  isThinking: boolean;
  isStreaming: boolean;
  hasSuggestions: boolean;
  currentStreamingText: string;
  aiSuggestions?: any[];
  icps?: any[];
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  icps: () => [],
  speakers: () => [],
  selectedMicId: "",
  selectedSpeakerId: "",
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "proceed-with-ai", icpId?: string): void;
  // icpId is used for the AI call summary even when the live assistant is off.
  (e: "proceed-without-ai", icpId?: string): void;
  (e: "answer-incoming-call"): void;
  (e: "decline-incoming-call"): void;
  (e: "hangup-incoming-call"): void;
  (e: "hangup"): void;
  (e: "toggle-record"): void;
  (e: "toggle-mute"): void;
  (e: "transfer-call", phoneNumber: string): void;
  (e: "select-microphone", deviceId: string): void;
  (e: "select-speaker", deviceId: string): void;
  (e: "send-digit", char: string): void;
}>();

const activeCallStates = computed(() => ["connecting", "ringing", "in-call", "reconnecting"]);

// Ringtone for the incoming-call-from-a-known-contact section above — this
// modal previously had no sound at all while ringing.
const RINGING_SOUND_URL = new URL("../../assets/audio/ringing-new.m4a", import.meta.url).href;
const audioRef = ref<HTMLAudioElement | null>(null);

const initAudio = () => {
  if (audioRef.value) return;
  try {
    audioRef.value = new Audio(RINGING_SOUND_URL);
    audioRef.value.loop = true;
    audioRef.value.volume = 0.7;
  } catch (error) {
    console.error("Failed to initialize ringtone audio:", error);
  }
};

const playRingingSound = async () => {
  if (!audioRef.value) initAudio();
  if (audioRef.value) {
    try {
      await audioRef.value.play();
    } catch (error) {
      console.warn("Ringtone playback failed (may require user interaction):", error);
    }
  }
};

const stopRingingSound = () => {
  if (audioRef.value) {
    audioRef.value.pause();
    audioRef.value.currentTime = 0;
  }
};

const handleAnswerIncoming = () => {
  stopRingingSound();
  emit("answer-incoming-call");
};

const handleDeclineIncoming = () => {
  stopRingingSound();
  emit("decline-incoming-call");
};

watch(
  () => props.incomingCallData?.callState,
  (callState) => {
    if (callState === "ringing") {
      playRingingSound();
    } else {
      stopRingingSound();
    }
  },
  { immediate: true }
);

// Modal stays narrow until the call is actually picked up.
// Narrow (440px): pre-call AI prompt, briefing-loading, connecting, ringing.
// Wide  (560px):  in-call, reconnecting, incoming-call (ringing/active).
const isNarrowState = computed(() => {
  if (props.incomingCallData) return false;
  if (props.showAIPrompt || props.isPreparingAIContext) return true;
  return props.status === "connecting" || props.status === "ringing";
});
const modalWidthPx = computed(() => (isNarrowState.value ? 440 : 560));

// Header identity helpers
const contactInitials = computed(() => {
  if (!props.contactName) return "";
  const parts = props.contactName.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts
    .map((p) => p.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);
});

const headerSubtitle = computed(() => {
  if (props.incomingCallData) {
    return props.incomingCallData.callState === "ringing"
      ? "Incoming call"
      : `In call · ${props.formattedIncomingCallTimer}`;
  }
  if (props.isPreparingAIContext) return "Preparing AI briefing…";
  if (props.showAIPrompt) return props.to || "Call Session";
  if (props.status === "connecting") return "Connecting…";
  if (props.status === "ringing") return "Ringing…";
  if (props.status === "in-call") return `In call · ${props.formattedCallTimer}`;
  if (props.status === "reconnecting") return "Reconnecting…";
  return props.to || "Call Session";
});

const headerStatusDot = computed(() => {
  if (props.incomingCallData) {
    return props.incomingCallData.callState === "ringing" ? "is-ringing" : "is-active";
  }
  if (props.status === "in-call") return "is-active";
  if (props.status === "ringing" || props.status === "connecting") return "is-ringing";
  if (props.status === "reconnecting") return "is-warning";
  return "";
});

// ICP options for the Select component
const icpOptions = computed(() =>
  (props.icps || []).map((i: any) => ({
    value: i.id,
    label: `${i.name}${i.isDefault ? " (Default)" : ""}`,
  }))
);

// Dropdowns and Inputs
const showIVRDropdown = ref(false);
const ivrInputValue = ref("");
const showMicrophoneDropdown = ref(false);
const showTransferPopover = ref(false);
const transferTarget = ref("");
const isTransferring = ref(false);
const selectedIcpId = ref<string | undefined>(undefined);

// Trigger refs (for keypad and transfer teleported popovers)
const keypadTriggerRef = ref<HTMLElement | null>(null);
const transferTriggerRef = ref<HTMLElement | null>(null);

// Inline styles for teleported popovers
const keypadPopoverStyle = ref<Record<string, string>>({});
const transferPopoverStyle = ref<Record<string, string>>({});

const computePopoverPosition = (
  triggerEl: HTMLElement | null,
  popoverWidth: number,
  popoverHeightEstimate: number
): Record<string, string> => {
  if (!triggerEl) return {};
  const rect = triggerEl.getBoundingClientRect();
  const spacing = 12;
  const spaceAbove = rect.top;
  const spaceBelow = window.innerHeight - rect.bottom;
  const placeBelow = spaceAbove < popoverHeightEstimate + spacing && spaceBelow > spaceAbove;
  let left = rect.left + rect.width / 2 - popoverWidth / 2;
  left = Math.max(8, Math.min(left, window.innerWidth - popoverWidth - 8));
  return {
    position: "fixed",
    top: placeBelow
      ? `${rect.bottom + spacing}px`
      : `${rect.top - popoverHeightEstimate - spacing}px`,
    left: `${left}px`,
    width: `${popoverWidth}px`,
    zIndex: "20100",
  };
};

const updateKeypadPosition = () => {
  keypadPopoverStyle.value = computePopoverPosition(keypadTriggerRef.value, 240, 320);
};
const updateTransferPosition = () => {
  transferPopoverStyle.value = computePopoverPosition(transferTriggerRef.value, 300, 220);
};

watch(showIVRDropdown, async (val) => {
  if (val) {
    showMicrophoneDropdown.value = false;
    showTransferPopover.value = false;
    await nextTick();
    updateKeypadPosition();
  }
});
watch(showMicrophoneDropdown, (val) => {
  if (val) {
    showIVRDropdown.value = false;
    showTransferPopover.value = false;
  }
});
watch(showTransferPopover, async (val) => {
  if (val) {
    showIVRDropdown.value = false;
    showMicrophoneDropdown.value = false;
    await nextTick();
    updateTransferPosition();
  } else {
    transferTarget.value = "";
  }
});

const onWindowReposition = () => {
  if (showIVRDropdown.value) updateKeypadPosition();
  if (showTransferPopover.value) updateTransferPosition();
};

onMounted(() => {
  window.addEventListener("scroll", onWindowReposition, true);
  window.addEventListener("resize", onWindowReposition);
});

const isValidTransferNumber = computed(() =>
  /^\+?\d{7,15}$/.test(transferTarget.value.replace(/\s+/g, ""))
);

const onSubmitTransfer = async () => {
  if (!isValidTransferNumber.value) return;
  isTransferring.value = true;
  try {
    emit("transfer-call", transferTarget.value.replace(/\s+/g, ""));
    showTransferPopover.value = false;
    transferTarget.value = "";
  } finally {
    isTransferring.value = false;
  }
};

const onSelectMicrophone = (deviceId: string) => {
  emit("select-microphone", deviceId);
  showMicrophoneDropdown.value = false;
};

const onSelectSpeaker = (deviceId: string) => {
  emit("select-speaker", deviceId);
};

// Initialize selectedIcpId with the default ICP if available
watch(
  () => props.icps,
  (newIcps) => {
    if (newIcps && newIcps.length > 0) {
      const defaultIcp = newIcps.find((i: any) => i.isDefault) || newIcps[0];
      selectedIcpId.value = defaultIcp.id;
    }
  },
  { immediate: true }
);

const handleIVRBtn = (char: string) => {
  ivrInputValue.value += char;
  emit("send-digit", char);
};

// --- Ref and Logic for Auto-Scrolling AI Suggestions ---
const suggestionsContainer = ref<HTMLElement | null>(null);

const scrollToBottom = async () => {
  await nextTick();
  if (suggestionsContainer.value) {
    suggestionsContainer.value.scrollTop = suggestionsContainer.value.scrollHeight;
  }
};

watch(
  () => props.aiSuggestions,
  () => {
    scrollToBottom();
  },
  { deep: true }
);

watch(
  () => props.currentStreamingText,
  () => {
    if (props.currentStreamingText) {
      scrollToBottom();
    }
  }
);

// Dragging logic
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

const modalStyle = computed(() => {
  if (isInitialPosition.value) {
    return {
      width: `${modalWidthPx.value}px`,
    };
  }
  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
    width: `${modalWidthPx.value}px`,
    zIndex: "20000",
  };
});

const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [
    ".btn-close-call",
    ".call-body",
    "a",
    "button",
    "input",
    ".keypad-popover",
    ".microphone-dropdown",
    ".transfer-popover",
    ".control-circle-btn",
    ".btn-hangup",
  ];
  if (excludedSelectors.some((selector) => target.closest(selector))) return;
  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;
  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;
  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - modalWidthPx.value) / 2,
        y: (window.innerHeight - 500) / 2,
      };
    }
    isInitialPosition.value = false;
  }
  dragStart.value = { x: clientX - modalPosition.value.x, y: clientY - modalPosition.value.y };
  const dragEvents = [
    ["mousemove", handleDrag, { passive: false }],
    ["mouseup", stopDrag],
    ["touchmove", handleDrag, { passive: false }],
    ["touchend", stopDrag],
  ] as const;
  dragEvents.forEach(([event, handler, options]) =>
    document.addEventListener(event, handler as any, options)
  );
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;
  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;
  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;
  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }
  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;
  ["mousemove", "mouseup", "touchmove", "touchend"].forEach((ev) =>
    document.removeEventListener(ev, handleDrag as any)
  );
};

const close = () => {
  stopRingingSound();
  if (
    activeCallStates.value.includes(props.status) ||
    props.isPreparingAIContext ||
    props.incomingCallData
  ) {
    emit("hangup");
    if (props.incomingCallData) {
      if (props.incomingCallData.callState === "ringing") emit("decline-incoming-call");
      else emit("hangup-incoming-call");
    }
  }
  isInitialPosition.value = true;
  modalPosition.value = { x: 0, y: 0 };
  emit("close");
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  if (
    showIVRDropdown.value &&
    !target.closest(".keypad-popover") &&
    target.closest(".control-circle-btn") !== keypadTriggerRef.value?.firstElementChild
  )
    showIVRDropdown.value = false;
  if (
    showTransferPopover.value &&
    !target.closest(".transfer-popover") &&
    target.closest(".control-circle-btn") !== transferTriggerRef.value?.firstElementChild
  )
    showTransferPopover.value = false;
};

onBeforeUnmount(() => {
  stopRingingSound();
  audioRef.value = null;
  ["mousemove", "mouseup", "touchmove", "touchend"].forEach((ev) =>
    document.removeEventListener(ev, handleDrag as any)
  );
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", onWindowReposition, true);
  window.removeEventListener("resize", onWindowReposition);
});

watch(
  () => props.show,
  (val) => {
    if (val) setTimeout(() => document.addEventListener("click", handleClickOutside), 0);
    else {
      document.removeEventListener("click", handleClickOutside);
      showIVRDropdown.value = false;
      showMicrophoneDropdown.value = false;
      showTransferPopover.value = false;
    }
  }
);
</script>

<style scoped>
.call-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  pointer-events: none;
}
.call-modal-overlay.dragging-active {
  align-items: flex-start;
  justify-content: flex-start;
}
.call-modal {
  background: #ffffff;
  border-radius: 12px;
  width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.35);
  user-select: none;
  pointer-events: auto;
  position: relative;
  font-family: "Inter", sans-serif;
  transition: width 250ms cubic-bezier(0.16, 1, 0.3, 1);
}
.call-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
  border-radius: 12px 12px 0 0;
  gap: 12px;
}
.call-header.draggable {
  cursor: grab;
}
.call-header-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}
.call-header-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.25);
}
.call-header-initials {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #ffffff;
}
.call-header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}
.call-header-name {
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.call-header-subtitle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}
.call-header-subtitle-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.78);
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.call-header-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.call-header-status-dot.is-active {
  background: #34d399;
  box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.25);
}
.call-header-status-dot.is-ringing {
  background: #fbbf24;
  animation: pulse 1.4s infinite;
}
.call-header-status-dot.is-warning {
  background: #f97316;
}
.call-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.header-icon-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  border: none;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.header-icon-btn:hover {
  background: rgba(255, 255, 255, 0.35);
  color: #ffffff;
}
.call-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

/* Incoming / Active Call States */
.incoming-call-section,
.active-incoming-call-section {
  background: #f8faff;
  border: 1px solid #e1e7ff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}
.incoming-call-header,
.active-call-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}
.incoming-call-indicator,
.active-call-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #3e3efe;
}
.pulse-dot {
  width: 8px;
  height: 8px;
  background: #3e3efe;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}
.active-dot {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
}
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
.incoming-call-from {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}
.incoming-call-actions,
.active-call-actions {
  display: flex;
  gap: 12px;
}
.incoming-call-caption-box {
  margin-top: 15px;
  background: white;
  border-radius: 8px;
  padding: 10px;
  border: 1px solid #e5e7eb;
}

.live-caption-container-box {
  background: #ffffff;
  border: 1px solid #f3f4f6;
  border-radius: 12px;
  padding: 16px;
  min-height: 120px;
  margin-bottom: 16px;
}

/* AI Preparations Section */
.ai-call-prompt-loading {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 12px;
}
.ai-call-prompt-loading-text {
  flex: 1;
}

/* Controls Row - "Original" Inline Look */
.call-controls-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 0 4px;
}
.control-item-original {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
}
.control-label-original {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
}

/* Sized control-circle buttons (smaller circles, larger inner icons) */
.control-circle-btn {
  width: 44px !important;
  height: 44px !important;
}

/* Icon size increase */
.call-controls-row :deep(.ui-button__icon .iconify) {
  font-size: 22px !important;
}

/* AI Copilot Prompt Card */
.ai-call-prompt-card {
  background: #f8faff;
  border: 1px solid #e1e7ff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(62, 62, 254, 0.05);
}
.ai-call-prompt-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.ai-icon {
  color: #3e3efe;
}
.ai-header-title {
  font-size: 15px;
  font-weight: 700;
  color: #3e3efe;
}
.ai-call-prompt-text {
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
  margin-bottom: 16px;
}
.ai-call-prompt-text.muted {
  color: #6b7280;
}
.ai-call-prompt-actions {
  display: flex;
  gap: 10px;
}
.ai-call-prompt-icp-selection {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Dropdowns & Popovers (teleported to body — positioned via inline :style) */
.keypad-popover,
.microphone-dropdown,
.transfer-popover {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 16px;
  font-family: "Inter", sans-serif;
}
.keypad-input {
  width: 100%;
  padding: 10px;
  text-align: center;
  font-weight: 700;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 12px;
}
.keypad-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.dropdown-header {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f3f4f6;
}
.microphone-dropdown {
  position: absolute;
  bottom: calc(100% + 15px);
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 16px;
  z-index: 100;
  width: 240px;
}
.microphone-dropdown ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.microphone-dropdown li {
  padding: 10px;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
  border-radius: 6px;
}
.microphone-dropdown li:hover {
  background: #f3f4f6;
  color: #111827;
}

/* Transfer Popover */
.transfer-popover__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.transfer-popover__title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.transfer-popover__hint {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 12px;
  line-height: 1.4;
}
.transfer-popover__input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  font-family: inherit;
}
.transfer-popover__input:focus {
  border-color: #3e3efe;
  box-shadow: 0 0 0 3px rgba(62, 62, 254, 0.1);
}

/* AI Suggestion Bubble (Scrollable List) */
.ai-suggestion-bubble {
  background: #fbfbfe;
  border: 1px solid #c4b5fd;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.08);
  display: flex;
  flex-direction: column;
}

.suggestion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.robot-icon-wrapper {
  width: 24px;
  height: 24px;
  background: #ede9fe;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c3aed;
}

.suggestion-label {
  font-size: 11px;
  font-weight: 700;
  color: #7c3aed;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Scrollable Container */
.ai-suggestions-list {
  max-height: 260px;
  overflow-y: auto;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scroll-behavior: smooth;
}

.ai-suggestions-list::-webkit-scrollbar {
  width: 4px;
}
.ai-suggestions-list::-webkit-scrollbar-track {
  background: transparent;
}
.ai-suggestions-list::-webkit-scrollbar-thumb {
  background: #d8b4fe;
  border-radius: 4px;
}

.suggestion-item {
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  transition: background-color 0.2s ease;
}

.suggestion-item.is-latest {
  background: #f5f3ff; /* Soft primary background for the newest item */
  border-left: 4px solid #3e3efe;
}

.thinking-item {
  padding: 8px 10px;
  display: flex;
  align-items: center;
}

.suggestion-text {
  font-size: 13.5px;
  line-height: 1.5;
  color: #1f2937;
  margin: 0;
  white-space: pre-wrap;
}

.suggestion-text.muted {
  color: #9ca3af;
  font-style: italic;
}

.typing-cursor {
  color: #7c3aed;
  font-weight: bold;
  animation: blink 0.8s step-end infinite;
  margin-left: 2px;
}

/* Thinking Loader */
.thinking-loader {
  display: flex;
  gap: 3px;
}

.thinking-loader span {
  width: 5px;
  height: 5px;
  background: #7c3aed;
  border-radius: 50%;
  animation: thinking-bounce 1.4s infinite ease-in-out both;
}

.thinking-loader span:nth-child(1) {
  animation-delay: -0.32s;
}
.thinking-loader span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes thinking-bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes blink {
  from,
  to {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
